/**
 * Swimlane (flow / sequence) layout metrics — the single source of pixel
 * constants for the swimlane process views.
 *
 * The live SwimlaneView adopted this module (plan P4/P5 unification): it
 * derives the SAME metrics object, so the pure geometry pass, the headless
 * SVG export, and the live view share one set of values by construction
 * (pinned by app-shell's geometry-parity test).
 *
 * ## Configurable size (`fontScale` / `layoutScale`)
 *
 * `swimlaneMetrics({ fontScale, layoutScale })` produces the metric object.
 * Two independent knobs so callers can grow the *text* separately from the
 * *spatial* layout:
 *
 *   • **fontScale** multiplies everything a bigger font drags with it — the
 *     font sizes, their line-heights, and the fixed text-box heights (a
 *     terminator pill, a sequence `do` band) plus the text bands that reserve
 *     headroom for a label. Growing the font without these would overflow the
 *     probed `line-height: normal` boxes.
 *   • **layoutScale** multiplies the spatial frame — lane width, cell insets
 *     and paddings, frame gaps/insets, gateway/aux geometry, the arrow bands.
 *
 * Both default to 1. Values are NOT rounded: the live view and the headless
 * export derive from this same function, so any rounding would apply
 * identically to both — but exact values keep the geometry-parity math clean
 * (SVG emits 2dp coordinates; CSS accepts fractional px).
 *
 * Grid model: one CSS-grid column per lane (`LANE_WIDTH` px), one grid row
 * per layout `y`. The live view lets the browser size rows from their
 * content; `swimlaneGeometry` reproduces that sizing in pure math (see the
 * PROBED base values below for the two `line-height: normal` boxes whose
 * heights come from Chrome's Inter metrics rather than explicit CSS).
 */

// ── Base (unscaled) values ────────────────────────────────────────────────────
// The canonical pixel constants at scale 1. `swimlaneMetrics` multiplies each
// by the font- or layout-scale. Grouped by which knob drives it.

const BASE = {
  // Lanes & canvas (layout)
  LANE_WIDTH: 160,
  LANE_HEADER_H: 44,
  BOARD_RIGHT_PAD: 22,
  CANVAS_BOTTOM_PAD: 24,
  // Rows (layout)
  ROW_MIN_H: 48,
  GATEWAY_ROW_H: 24,
  ROW_BASE_PAD: 8,
  // Step blocks — layout
  BLOCK_INSET: 32,
  BLOCK_BORDER: 1,
  BLOCK_PAD_X: 6,
  BLOCK_PAD_Y: 3,
  BLOCK_RADIUS: 6,
  // Step blocks — font
  BLOCK_MIN_H: 22,
  BLOCK_FONT_SIZE: 11,
  BLOCK_LINE_H: 14,
  BLOCK_SUB_FONT_SIZE: 9,
  BLOCK_SUB_LINE_H: 11,
  BLOCK_SUB_MARGIN_TOP: 1,
  BLOCK_TAG_MARGIN_BOTTOM: 2,
  // Subprocess `do` bands
  DO_FLOW_PAD_X: 8, // layout
  DO_FLOW_PAD_Y: 4, // layout
  DO_FLOW_MIN_H: 22, // font
  DO_CARET_W: 7, // layout
  DO_CARET_GAP: 4, // layout
  DO_CARET_FONT_SIZE: 8, // font
  DO_LABEL_FONT_SIZE: 11, // font
  DO_SEQ_BAND_H: 25, // font (PROBED: border 2 + pad 10 + 13px Inter-11 normal box)
  DO_SEQ_INSET: 16, // layout
  DO_HINT_FONT_SIZE: 9, // font ("SUBPROCESS")
  // Terminator pills
  TERM_H: 17, // font (PROBED: pad 2·2 + 13px Inter-11 normal box)
  TERM_MIN_W: 60, // layout
  TERM_PAD_X: 10, // layout
  TERM_FONT_SIZE: 11, // font
  TERM_CHIP_GAP: 6, // layout
  TERM_CHIP_FONT_SIZE: 8, // font
  TERM_CHIP_PAD_X: 3, // layout
  TERM_CHIP_BORDER: 1, // layout
  TERM_CHIP_H: 13, // font (8px leading-none + 1.5·2 pad + 1·2 border)
  // Lane header chip — the label box inside the header band
  LANE_HEADER_CHIP_INSET_X: 20, // layout (chip x-inset from the lane edge)
  LANE_HEADER_CHIP_INSET_Y: 8, // layout (chip y-inset from the band edge)
  LANE_HEADER_CHIP_BORDER: 1, // layout
  LANE_HEADER_CHIP_PAD_X: 8, // layout
  LANE_HEADER_LINE_H: 12, // font (line box for a wrapped header line)
  // Lane header / frame / secondary label / annotation fonts
  LANE_HEADER_FONT_SIZE: 12, // font
  FRAME_LABEL_FONT_SIZE: 11, // font (a frame's title strip)
  LABEL_FONT_SIZE: 9, // font (branch tags, tab chips, leg labels, thresholds, +N)
  ANNOTATION_FONT_SIZE: 8, // font (dist, call comments, latency, ruler, re-entry)
  // Frames (layout)
  NESTED_INSET_X: 6,
  NESTED_INSET_Y: 3,
  FRAME_GAP: 6,
  FRAME_EDGE_INSET: 8,
  // Text bands (font — headroom for a label line)
  FRAME_TITLE_BAND: 16,
  LABEL_BAND: 11,
  COMMENT_BAND: 16,
  EACH_BADGE_BAND: 12,
  SEQ_FRAME_BOTTOM_PAD: 8,
  // Arrow bands (layout)
  ARROW_BAND: 7,
  // Gateways / aux geometry (layout)
  GW_HALF: 9,
  OVER_RISE: 12,
  AUX_HEAD: 13,
  GUTTER_BASE: 6,
  GUTTER_TRACK: 5,
} as const;

/** Chrome's `line-height: normal` box for 11px Inter (PROBED) — the base feed
 *  for the two probed box heights; a font-scaled derivation for renderers that
 *  need baseline math. */
const INTER_NORMAL_LINE_11_BASE = 13;

/** Scalar counts / char budgets — never scaled. */
export const ARROW_BAND_MAX = 4;
export const COMMENT_LABEL_MAX = 26;
/** Wrapped-line cap for a lane-header chip — two 12px lines are what the
 *  44px header band holds. Overflow is ellipsized; the full name stays on the
 *  chip's `title` (live) / group title (export). */
export const LANE_HEADER_MAX_LINES = 2;
/** Wrapped-line cap for a step block's primary label and its subtitle. Without
 *  it a single seam-broken identifier (a long underscore-joined module name)
 *  grows the row unboundedly. Set above the deepest wrap real prose needs at
 *  `BLOCK_WRAP_W` (a full Cyrillic sentence takes 5), so the cap only ever
 *  bites a pathological identifier. */
export const BLOCK_MAX_LINES = 5;

// ── The metric object ─────────────────────────────────────────────────────────

export interface SwimlaneScale {
  /** Font sizes, line-heights, text-box heights, label bands. Default 1. */
  readonly fontScale?: number;
  /** Lane width, insets/paddings, gaps, gateway/aux geometry. Default 1. */
  readonly layoutScale?: number;
}

export interface SwimlaneMetrics {
  readonly fontScale: number;
  readonly layoutScale: number;

  readonly LANE_WIDTH: number;
  readonly LANE_HEADER_H: number;
  readonly BOARD_RIGHT_PAD: number;
  readonly CANVAS_BOTTOM_PAD: number;
  readonly ROW_MIN_H: number;
  readonly GATEWAY_ROW_H: number;
  readonly ROW_BASE_PAD: number;
  readonly BLOCK_INSET: number;
  readonly BLOCK_W: number;
  readonly BLOCK_BORDER: number;
  readonly BLOCK_PAD_X: number;
  readonly BLOCK_PAD_Y: number;
  readonly BLOCK_WRAP_W: number;
  readonly BLOCK_MIN_H: number;
  readonly BLOCK_RADIUS: number;
  readonly BLOCK_FONT_SIZE: number;
  readonly BLOCK_LINE_H: number;
  readonly BLOCK_SUB_FONT_SIZE: number;
  readonly BLOCK_SUB_LINE_H: number;
  readonly BLOCK_SUB_MARGIN_TOP: number;
  readonly BLOCK_TAG_MARGIN_BOTTOM: number;
  readonly TRIGGER_MARKER_W: number;
  readonly DO_FLOW_PAD_X: number;
  readonly DO_FLOW_PAD_Y: number;
  readonly DO_FLOW_MIN_H: number;
  readonly DO_CARET_W: number;
  readonly DO_CARET_GAP: number;
  readonly DO_CARET_FONT_SIZE: number;
  readonly DO_LABEL_FONT_SIZE: number;
  readonly DO_SEQ_BAND_H: number;
  readonly DO_SEQ_INSET: number;
  readonly DO_HINT_FONT_SIZE: number;
  readonly TERM_H: number;
  readonly TERM_MIN_W: number;
  readonly TERM_PAD_X: number;
  readonly TERM_FONT_SIZE: number;
  readonly TERM_CHIP_GAP: number;
  readonly TERM_CHIP_FONT_SIZE: number;
  readonly TERM_CHIP_PAD_X: number;
  readonly TERM_CHIP_BORDER: number;
  readonly TERM_CHIP_H: number;
  readonly LANE_HEADER_CHIP_INSET_X: number;
  readonly LANE_HEADER_CHIP_INSET_Y: number;
  readonly LANE_HEADER_CHIP_BORDER: number;
  readonly LANE_HEADER_CHIP_PAD_X: number;
  /** Chip box width (`LANE_WIDTH - 2·inset`). */
  readonly LANE_HEADER_CHIP_W: number;
  /** Text budget inside the chip (chip width less its border + padding). */
  readonly LANE_HEADER_TEXT_W: number;
  readonly LANE_HEADER_LINE_H: number;
  readonly LANE_HEADER_MAX_LINES: number;
  readonly BLOCK_MAX_LINES: number;
  readonly LANE_HEADER_FONT_SIZE: number;
  readonly FRAME_LABEL_FONT_SIZE: number;
  readonly LABEL_FONT_SIZE: number;
  readonly ANNOTATION_FONT_SIZE: number;
  readonly INTER_NORMAL_LINE_11: number;
  readonly NESTED_INSET_X: number;
  readonly NESTED_INSET_Y: number;
  readonly FRAME_GAP: number;
  readonly FRAME_TITLE_BAND: number;
  readonly FRAME_EDGE_INSET: number;
  readonly ARROW_BAND: number;
  readonly ARROW_BAND_MAX: number;
  readonly LABEL_BAND: number;
  readonly COMMENT_BAND: number;
  readonly EACH_BADGE_BAND: number;
  readonly SEQ_FRAME_TOP_PAD: number;
  readonly SEQ_FRAME_BOTTOM_PAD: number;
  readonly GW_HALF: number;
  readonly OVER_RISE: number;
  readonly AUX_HEAD: number;
  readonly GUTTER_BASE: number;
  readonly GUTTER_TRACK: number;
  readonly COMMENT_LABEL_MAX: number;
}

/**
 * Produce the swimlane metric set at the given scales. `fontScale` grows text
 * (and its line-heights / text-box heights / label bands); `layoutScale` grows
 * the spatial frame (lane width, insets, gaps, gateway geometry). See the
 * module docstring for which knob drives each value.
 */
export function swimlaneMetrics(scale: SwimlaneScale = {}): SwimlaneMetrics {
  const fontScale = scale.fontScale ?? 1;
  const layoutScale = scale.layoutScale ?? 1;
  const f = (n: number): number => n * fontScale;
  const l = (n: number): number => n * layoutScale;

  const LANE_WIDTH = l(BASE.LANE_WIDTH);
  const BLOCK_INSET = l(BASE.BLOCK_INSET);
  const BLOCK_BORDER = l(BASE.BLOCK_BORDER);
  const BLOCK_PAD_X = l(BASE.BLOCK_PAD_X);
  const BLOCK_W = LANE_WIDTH - 2 * BLOCK_INSET;
  const BLOCK_WRAP_W = BLOCK_W - 2 * BLOCK_BORDER - 2 * BLOCK_PAD_X;

  // The START-trigger `▶` marker width the geometry steals from a trigger
  // block's first line: the marker renders at the 9px subtitle size, absent
  // from every metric table → 0.6em fallback glyph, plus a 4px (layout)
  // margin. 0.6·9 + 4 = 9.4 at scale 1.
  const TRIGGER_MARKER_W = 0.6 * f(BASE.BLOCK_SUB_FONT_SIZE) + l(4);

  const FRAME_TITLE_BAND = f(BASE.FRAME_TITLE_BAND);

  const LANE_HEADER_CHIP_INSET_X = l(BASE.LANE_HEADER_CHIP_INSET_X);
  const LANE_HEADER_CHIP_BORDER = l(BASE.LANE_HEADER_CHIP_BORDER);
  const LANE_HEADER_CHIP_PAD_X = l(BASE.LANE_HEADER_CHIP_PAD_X);
  const LANE_HEADER_CHIP_W = LANE_WIDTH - 2 * LANE_HEADER_CHIP_INSET_X;
  const LANE_HEADER_TEXT_W =
    LANE_HEADER_CHIP_W - 2 * LANE_HEADER_CHIP_BORDER - 2 * LANE_HEADER_CHIP_PAD_X;

  return {
    fontScale,
    layoutScale,

    LANE_WIDTH,
    LANE_HEADER_H: l(BASE.LANE_HEADER_H),
    BOARD_RIGHT_PAD: l(BASE.BOARD_RIGHT_PAD),
    CANVAS_BOTTOM_PAD: l(BASE.CANVAS_BOTTOM_PAD),
    ROW_MIN_H: l(BASE.ROW_MIN_H),
    GATEWAY_ROW_H: l(BASE.GATEWAY_ROW_H),
    ROW_BASE_PAD: l(BASE.ROW_BASE_PAD),
    BLOCK_INSET,
    BLOCK_W,
    BLOCK_BORDER,
    BLOCK_PAD_X,
    BLOCK_PAD_Y: l(BASE.BLOCK_PAD_Y),
    BLOCK_WRAP_W,
    BLOCK_MIN_H: f(BASE.BLOCK_MIN_H),
    BLOCK_RADIUS: l(BASE.BLOCK_RADIUS),
    BLOCK_FONT_SIZE: f(BASE.BLOCK_FONT_SIZE),
    BLOCK_LINE_H: f(BASE.BLOCK_LINE_H),
    BLOCK_SUB_FONT_SIZE: f(BASE.BLOCK_SUB_FONT_SIZE),
    BLOCK_SUB_LINE_H: f(BASE.BLOCK_SUB_LINE_H),
    BLOCK_SUB_MARGIN_TOP: f(BASE.BLOCK_SUB_MARGIN_TOP),
    BLOCK_TAG_MARGIN_BOTTOM: f(BASE.BLOCK_TAG_MARGIN_BOTTOM),
    TRIGGER_MARKER_W,
    DO_FLOW_PAD_X: l(BASE.DO_FLOW_PAD_X),
    DO_FLOW_PAD_Y: l(BASE.DO_FLOW_PAD_Y),
    DO_FLOW_MIN_H: f(BASE.DO_FLOW_MIN_H),
    DO_CARET_W: l(BASE.DO_CARET_W),
    DO_CARET_GAP: l(BASE.DO_CARET_GAP),
    DO_CARET_FONT_SIZE: f(BASE.DO_CARET_FONT_SIZE),
    DO_LABEL_FONT_SIZE: f(BASE.DO_LABEL_FONT_SIZE),
    DO_SEQ_BAND_H: f(BASE.DO_SEQ_BAND_H),
    DO_SEQ_INSET: l(BASE.DO_SEQ_INSET),
    DO_HINT_FONT_SIZE: f(BASE.DO_HINT_FONT_SIZE),
    TERM_H: f(BASE.TERM_H),
    TERM_MIN_W: l(BASE.TERM_MIN_W),
    TERM_PAD_X: l(BASE.TERM_PAD_X),
    TERM_FONT_SIZE: f(BASE.TERM_FONT_SIZE),
    TERM_CHIP_GAP: l(BASE.TERM_CHIP_GAP),
    TERM_CHIP_FONT_SIZE: f(BASE.TERM_CHIP_FONT_SIZE),
    TERM_CHIP_PAD_X: l(BASE.TERM_CHIP_PAD_X),
    TERM_CHIP_BORDER: l(BASE.TERM_CHIP_BORDER),
    TERM_CHIP_H: f(BASE.TERM_CHIP_H),
    LANE_HEADER_CHIP_INSET_X,
    LANE_HEADER_CHIP_INSET_Y: l(BASE.LANE_HEADER_CHIP_INSET_Y),
    LANE_HEADER_CHIP_BORDER,
    LANE_HEADER_CHIP_PAD_X,
    LANE_HEADER_CHIP_W,
    LANE_HEADER_TEXT_W,
    LANE_HEADER_LINE_H: f(BASE.LANE_HEADER_LINE_H),
    LANE_HEADER_MAX_LINES,
    BLOCK_MAX_LINES,
    LANE_HEADER_FONT_SIZE: f(BASE.LANE_HEADER_FONT_SIZE),
    FRAME_LABEL_FONT_SIZE: f(BASE.FRAME_LABEL_FONT_SIZE),
    LABEL_FONT_SIZE: f(BASE.LABEL_FONT_SIZE),
    ANNOTATION_FONT_SIZE: f(BASE.ANNOTATION_FONT_SIZE),
    INTER_NORMAL_LINE_11: f(INTER_NORMAL_LINE_11_BASE),
    NESTED_INSET_X: l(BASE.NESTED_INSET_X),
    NESTED_INSET_Y: l(BASE.NESTED_INSET_Y),
    FRAME_GAP: l(BASE.FRAME_GAP),
    FRAME_TITLE_BAND,
    FRAME_EDGE_INSET: l(BASE.FRAME_EDGE_INSET),
    ARROW_BAND: l(BASE.ARROW_BAND),
    ARROW_BAND_MAX,
    LABEL_BAND: f(BASE.LABEL_BAND),
    COMMENT_BAND: f(BASE.COMMENT_BAND),
    EACH_BADGE_BAND: f(BASE.EACH_BADGE_BAND),
    SEQ_FRAME_TOP_PAD: FRAME_TITLE_BAND + f(4),
    SEQ_FRAME_BOTTOM_PAD: f(BASE.SEQ_FRAME_BOTTOM_PAD),
    GW_HALF: l(BASE.GW_HALF),
    OVER_RISE: l(BASE.OVER_RISE),
    AUX_HEAD: l(BASE.AUX_HEAD),
    GUTTER_BASE: l(BASE.GUTTER_BASE),
    GUTTER_TRACK: l(BASE.GUTTER_TRACK),
    COMMENT_LABEL_MAX,
  };
}

/** The default (scale-1) metric set — for callers that render at native size. */
export const DEFAULT_SWIMLANE_METRICS = swimlaneMetrics();

// ── Flat scale-1 exports (back-compat) ────────────────────────────────────────
// The canonical values at scale 1, for call sites that render at native size
// and don't thread a scale. Callers that scale derive their own set via
// `swimlaneMetrics({ fontScale, layoutScale })`.
export const LANE_WIDTH = DEFAULT_SWIMLANE_METRICS.LANE_WIDTH;
export const LANE_HEADER_H = DEFAULT_SWIMLANE_METRICS.LANE_HEADER_H;
export const BOARD_RIGHT_PAD = DEFAULT_SWIMLANE_METRICS.BOARD_RIGHT_PAD;
export const CANVAS_BOTTOM_PAD = DEFAULT_SWIMLANE_METRICS.CANVAS_BOTTOM_PAD;
export const ROW_MIN_H = DEFAULT_SWIMLANE_METRICS.ROW_MIN_H;
export const GATEWAY_ROW_H = DEFAULT_SWIMLANE_METRICS.GATEWAY_ROW_H;
export const ROW_BASE_PAD = DEFAULT_SWIMLANE_METRICS.ROW_BASE_PAD;
export const BLOCK_INSET = DEFAULT_SWIMLANE_METRICS.BLOCK_INSET;
export const BLOCK_W = DEFAULT_SWIMLANE_METRICS.BLOCK_W;
export const BLOCK_BORDER = DEFAULT_SWIMLANE_METRICS.BLOCK_BORDER;
export const BLOCK_PAD_X = DEFAULT_SWIMLANE_METRICS.BLOCK_PAD_X;
export const BLOCK_PAD_Y = DEFAULT_SWIMLANE_METRICS.BLOCK_PAD_Y;
export const BLOCK_WRAP_W = DEFAULT_SWIMLANE_METRICS.BLOCK_WRAP_W;
export const BLOCK_MIN_H = DEFAULT_SWIMLANE_METRICS.BLOCK_MIN_H;
export const BLOCK_RADIUS = DEFAULT_SWIMLANE_METRICS.BLOCK_RADIUS;
export const BLOCK_FONT_SIZE = DEFAULT_SWIMLANE_METRICS.BLOCK_FONT_SIZE;
export const BLOCK_LINE_H = DEFAULT_SWIMLANE_METRICS.BLOCK_LINE_H;
export const BLOCK_SUB_FONT_SIZE = DEFAULT_SWIMLANE_METRICS.BLOCK_SUB_FONT_SIZE;
export const BLOCK_SUB_LINE_H = DEFAULT_SWIMLANE_METRICS.BLOCK_SUB_LINE_H;
export const BLOCK_SUB_MARGIN_TOP = DEFAULT_SWIMLANE_METRICS.BLOCK_SUB_MARGIN_TOP;
export const BLOCK_TAG_MARGIN_BOTTOM = DEFAULT_SWIMLANE_METRICS.BLOCK_TAG_MARGIN_BOTTOM;
export const TRIGGER_MARKER_W = DEFAULT_SWIMLANE_METRICS.TRIGGER_MARKER_W;
export const DO_FLOW_PAD_X = DEFAULT_SWIMLANE_METRICS.DO_FLOW_PAD_X;
export const DO_FLOW_PAD_Y = DEFAULT_SWIMLANE_METRICS.DO_FLOW_PAD_Y;
export const DO_FLOW_MIN_H = DEFAULT_SWIMLANE_METRICS.DO_FLOW_MIN_H;
export const DO_CARET_W = DEFAULT_SWIMLANE_METRICS.DO_CARET_W;
export const DO_CARET_GAP = DEFAULT_SWIMLANE_METRICS.DO_CARET_GAP;
export const DO_SEQ_BAND_H = DEFAULT_SWIMLANE_METRICS.DO_SEQ_BAND_H;
export const DO_SEQ_INSET = DEFAULT_SWIMLANE_METRICS.DO_SEQ_INSET;
export const TERM_H = DEFAULT_SWIMLANE_METRICS.TERM_H;
export const TERM_MIN_W = DEFAULT_SWIMLANE_METRICS.TERM_MIN_W;
export const TERM_PAD_X = DEFAULT_SWIMLANE_METRICS.TERM_PAD_X;
export const TERM_FONT_SIZE = DEFAULT_SWIMLANE_METRICS.TERM_FONT_SIZE;
export const TERM_CHIP_GAP = DEFAULT_SWIMLANE_METRICS.TERM_CHIP_GAP;
export const TERM_CHIP_FONT_SIZE = DEFAULT_SWIMLANE_METRICS.TERM_CHIP_FONT_SIZE;
export const TERM_CHIP_PAD_X = DEFAULT_SWIMLANE_METRICS.TERM_CHIP_PAD_X;
export const TERM_CHIP_BORDER = DEFAULT_SWIMLANE_METRICS.TERM_CHIP_BORDER;
export const TERM_CHIP_H = DEFAULT_SWIMLANE_METRICS.TERM_CHIP_H;
export const LANE_HEADER_FONT_SIZE = DEFAULT_SWIMLANE_METRICS.LANE_HEADER_FONT_SIZE;
export const LANE_HEADER_CHIP_INSET_X = DEFAULT_SWIMLANE_METRICS.LANE_HEADER_CHIP_INSET_X;
export const LANE_HEADER_CHIP_INSET_Y = DEFAULT_SWIMLANE_METRICS.LANE_HEADER_CHIP_INSET_Y;
export const LANE_HEADER_CHIP_BORDER = DEFAULT_SWIMLANE_METRICS.LANE_HEADER_CHIP_BORDER;
export const LANE_HEADER_CHIP_PAD_X = DEFAULT_SWIMLANE_METRICS.LANE_HEADER_CHIP_PAD_X;
export const LANE_HEADER_CHIP_W = DEFAULT_SWIMLANE_METRICS.LANE_HEADER_CHIP_W;
export const LANE_HEADER_TEXT_W = DEFAULT_SWIMLANE_METRICS.LANE_HEADER_TEXT_W;
export const LANE_HEADER_LINE_H = DEFAULT_SWIMLANE_METRICS.LANE_HEADER_LINE_H;
export const INTER_NORMAL_LINE_11 = DEFAULT_SWIMLANE_METRICS.INTER_NORMAL_LINE_11;
export const NESTED_INSET_X = DEFAULT_SWIMLANE_METRICS.NESTED_INSET_X;
export const NESTED_INSET_Y = DEFAULT_SWIMLANE_METRICS.NESTED_INSET_Y;
export const FRAME_GAP = DEFAULT_SWIMLANE_METRICS.FRAME_GAP;
export const FRAME_TITLE_BAND = DEFAULT_SWIMLANE_METRICS.FRAME_TITLE_BAND;
export const FRAME_EDGE_INSET = DEFAULT_SWIMLANE_METRICS.FRAME_EDGE_INSET;
export const ARROW_BAND = DEFAULT_SWIMLANE_METRICS.ARROW_BAND;
export const LABEL_BAND = DEFAULT_SWIMLANE_METRICS.LABEL_BAND;
export const COMMENT_BAND = DEFAULT_SWIMLANE_METRICS.COMMENT_BAND;
export const EACH_BADGE_BAND = DEFAULT_SWIMLANE_METRICS.EACH_BADGE_BAND;
export const SEQ_FRAME_TOP_PAD = DEFAULT_SWIMLANE_METRICS.SEQ_FRAME_TOP_PAD;
export const SEQ_FRAME_BOTTOM_PAD = DEFAULT_SWIMLANE_METRICS.SEQ_FRAME_BOTTOM_PAD;
export const GW_HALF = DEFAULT_SWIMLANE_METRICS.GW_HALF;
export const OVER_RISE = DEFAULT_SWIMLANE_METRICS.OVER_RISE;
export const AUX_HEAD = DEFAULT_SWIMLANE_METRICS.AUX_HEAD;
export const GUTTER_BASE = DEFAULT_SWIMLANE_METRICS.GUTTER_BASE;
export const GUTTER_TRACK = DEFAULT_SWIMLANE_METRICS.GUTTER_TRACK;
